import React, { useCallback, useMemo } from 'react';

// Debug system is now managed at the application level via DebugFeatureProvider
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import styled from 'styled-components';

import { BreakpointSelectors } from '../theme';
import { DEFAULT_PANEL_CONFIG } from './constants';
import { useLayoutStrategy } from './contexts/LayoutStrategyContext';
import { useViewportSize } from './contexts/ViewportSizeContext';
import { useAutoLayout } from './hooks/useAutoLayout';
import { useResponsiveMode } from './hooks/useResponsive';
import { ContentVisibilityGuard } from './services/ContentVisibilityGuard';
import { AutoPanelLayoutProps, LayoutArea, ResponsiveMode } from './types';
import { useComponentCache } from './utils/componentCache';
import { isValidContent } from './utils/contentDetection';

/**
 * Enhanced AutoPanelLayout component that supports both traditional panel layouts
 * and tab-based layouts (replacing PageWithTabsEnhanced functionality).
 *
 * Performance optimized with React.memo and stable prop handling.
 */
const AutoPanelLayoutTabbedRenderer: React.FC<AutoPanelLayoutProps> = function AutoPanelLayoutTabbedRenderer({
  children,
  autoSaveId,
  direction = 'horizontal',
  className,
  responsive: _responsive,
  panelConfig,
  onPanelChange,
  // Tab-based layout props
  tabs,
  secondaryTabs,
  actionButtons,
  header,
  breadcrumb,
  subheader,
  footer,
  mobileConfig = {},
  layoutConfig = {},
}: AutoPanelLayoutProps) {
  // Initialize component caching for fast tab switching
  const _componentCache = useComponentCache();

  const { panelStates, activePanels, setPanel } = useAutoLayout();
  const responsiveMode = useResponsiveMode();
  const { viewport } = useViewportSize();
  const { recalculateStrategy, strategy } = useLayoutStrategy();

  // Generate unique grid area suffix to prevent conflicts between multiple AutoPanelLayouts
  const instanceId = useMemo(() => autoSaveId ?? `auto-panel-${Date.now()}`, [autoSaveId]);

  // Helper function to create unique grid area names
  const getUniqueGridArea = useCallback((area: string) => `${area}-${instanceId}`, [instanceId]);

  // Access unified debug system
  // Debug system moved to application-level DebugFeatureProvider

  const { sidebarMode = 'overlay', collapseTabs: _collapseTabs = false, compactSpacing = false, hideActionsOnMobile = false } = mobileConfig;

  const { gridTemplateAreas, gridTemplateColumns, gridTemplateRows, noSpacing = false } = layoutConfig ?? {};

  const isMobile = responsiveMode === ResponsiveMode.MOBILE;

  // Determine if this is a tab-based layout or traditional panel layout
  const isTabLayout = !!(tabs ?? header ?? breadcrumb ?? subheader ?? actionButtons ?? footer);

  // Merge panel configurations
  const finalPanelConfig = useMemo(
    () => ({
      ...DEFAULT_PANEL_CONFIG,
      ...panelConfig,
    }),
    [panelConfig],
  );

  // For tab-based layouts, automatically set tab content based on props
  React.useEffect(() => {
    if (isTabLayout) {
      if (header) setPanel(LayoutArea.HEADER, header);
      if (breadcrumb) setPanel(LayoutArea.BREADCRUMB, breadcrumb);
      if (subheader) setPanel(LayoutArea.SUBHEADER, subheader);
      if (tabs) setPanel(LayoutArea.TABS, tabs);
      if (secondaryTabs) setPanel(LayoutArea.SECONDARY_TABS, secondaryTabs);
      if (actionButtons) setPanel(LayoutArea.ACTION_BUTTONS, actionButtons);
      if (footer) setPanel(LayoutArea.FOOTER, footer);
    }
  }, [header, breadcrumb, subheader, tabs, secondaryTabs, actionButtons, footer, setPanel, isTabLayout]);

  // Get visible panels and content with visibility guard integration
  const {
    visiblePanels,
    fallbackAreas: _fallbackAreas,
    visibilityAdjustments: _visibilityAdjustments,
  } = useMemo(() => {
    const candidatePanels = activePanels.filter((area) => {
      const state = panelStates.get(area);
      return state?.isVisible && isValidContent(state.content);
    });

    // Apply visibility constraints
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
      availableWidth: window.innerWidth - 100, // Account for margins/padding
      availableHeight: window.innerHeight - 100,
      mode: responsiveMode,
    };

    const visibilityResult = ContentVisibilityGuard.ensureContentVisibility(candidatePanels, viewport);

    return {
      visiblePanels: visibilityResult.visibleAreas,
      fallbackAreas: visibilityResult.fallbackAreas,
      visibilityAdjustments: visibilityResult.adjustments,
    };
  }, [activePanels, panelStates, responsiveMode]);

  // Generate grid layout for tab-based layouts
  const getTabGridLayout = () => {
    if (!isTabLayout) return null;

    const hasLeftSidebar = visiblePanels.includes(LayoutArea.LEFT_SIDEBAR);
    const hasRightSidebar = visiblePanels.includes(LayoutArea.RIGHT_SIDEBAR);
    const _hasSidebars = hasLeftSidebar || hasRightSidebar;

    // Mobile sidebar visibility logic
    const _showLeftSidebarOnMobile = hasLeftSidebar && (sidebarMode === 'stack' || !isMobile);
    const _showRightSidebarOnMobile = hasRightSidebar && (sidebarMode === 'stack' || !isMobile);
    const showActionsOnMobile = actionButtons && !hideActionsOnMobile;

    // Generate grid template areas based on layout
    const getGridTemplateAreas = () => {
      if (gridTemplateAreas) return gridTemplateAreas;

      if (isMobile && sidebarMode === 'stack') {
        // Mobile stacked layout with unique grid area names
        let areas = '';
        if (breadcrumb) areas += `"${getUniqueGridArea('breadcrumb')} ${getUniqueGridArea('breadcrumb')} ${getUniqueGridArea('breadcrumb')}"\n`;
        if (header) areas += `"${getUniqueGridArea('header')} ${getUniqueGridArea('header')} ${getUniqueGridArea('header')}"\n`;
        if (subheader) areas += `"${getUniqueGridArea('subheader')} ${getUniqueGridArea('subheader')} ${getUniqueGridArea('subheader')}"\n`;
        areas += `"${getUniqueGridArea('tabs')} ${getUniqueGridArea('tabs')} ${getUniqueGridArea('tabs')}"\n`;
        if (secondaryTabs) areas += `"${getUniqueGridArea('secondary-tabs')} ${getUniqueGridArea('secondary-tabs')} ${getUniqueGridArea('secondary-tabs')}"\n`;
        if (hasLeftSidebar) areas += `"${getUniqueGridArea('left-sidebar')} ${getUniqueGridArea('left-sidebar')} ${getUniqueGridArea('left-sidebar')}"\n`;
        if (actionButtons && showActionsOnMobile)
          areas += `"${getUniqueGridArea('action-buttons')} ${getUniqueGridArea('action-buttons')} ${getUniqueGridArea('action-buttons')}"\n`;
        areas += `"${getUniqueGridArea('content')} ${getUniqueGridArea('content')} ${getUniqueGridArea('content')}"`;
        if (hasRightSidebar) areas += `\n"${getUniqueGridArea('right-sidebar')} ${getUniqueGridArea('right-sidebar')} ${getUniqueGridArea('right-sidebar')}"`;
        if (footer) areas += `\n"${getUniqueGridArea('footer')} ${getUniqueGridArea('footer')} ${getUniqueGridArea('footer')}"`;
        return areas;
      }

      // Desktop or overlay layout with unique grid area names
      const columns = [];
      if (hasLeftSidebar && !isMobile) columns.push(getUniqueGridArea('left-sidebar'));
      columns.push(getUniqueGridArea('content'));
      if (hasRightSidebar && !isMobile) columns.push(getUniqueGridArea('right-sidebar'));

      let areas = '';
      const columnSpan = columns.join(' ');

      if (breadcrumb) areas += `"${columns.map(() => getUniqueGridArea('breadcrumb')).join(' ')}"\n`;
      if (header) areas += `"${columns.map(() => getUniqueGridArea('header')).join(' ')}"\n`;
      if (subheader) areas += `"${columns.map(() => getUniqueGridArea('subheader')).join(' ')}"\n`;
      areas += `"${columns.map(() => getUniqueGridArea('tabs')).join(' ')}"\n`;
      if (secondaryTabs) areas += `"${columns.map(() => getUniqueGridArea('secondary-tabs')).join(' ')}"\n`;
      if (actionButtons && showActionsOnMobile) areas += `"${columns.map(() => getUniqueGridArea('action-buttons')).join(' ')}"\n`;
      areas += `"${columnSpan}"`;
      if (footer) areas += `\n"${columns.map(() => getUniqueGridArea('footer')).join(' ')}"`;

      return areas;
    };

    // Generate grid template columns using dynamic calculation
    const getGridTemplateColumns = () => {
      if (gridTemplateColumns) return gridTemplateColumns;

      // Simple fallback - use auto for columns
      return 'auto';
    };

    // Generate grid template rows using dynamic calculation
    const getGridTemplateRows = () => {
      if (gridTemplateRows) return gridTemplateRows;

      // Simple fallback - use auto for rows
      return 'auto';
    };

    return {
      gridTemplateAreas: getGridTemplateAreas(),
      gridTemplateColumns: getGridTemplateColumns(),
      gridTemplateRows: getGridTemplateRows(),
    };
  };

  const gridLayout = getTabGridLayout();

  if (isTabLayout) {
    return (
      <StyledTabbedLayout
        className={className}
        $isMobile={isMobile}
        $hasLeftSidebar={visiblePanels.includes(LayoutArea.LEFT_SIDEBAR)}
        $hasRightSidebar={visiblePanels.includes(LayoutArea.RIGHT_SIDEBAR)}
        $sidebarMode={sidebarMode}
        $compactSpacing={compactSpacing}
        $noSpacing={noSpacing}
        style={{
          gridTemplateAreas: gridLayout?.gridTemplateAreas,
          gridTemplateColumns: gridLayout?.gridTemplateColumns,
          gridTemplateRows: gridLayout?.gridTemplateRows,
        }}
      >
        {/* Render grid areas */}
        {visiblePanels.map((area) => {
          const state = panelStates.get(area);
          if (!state?.content) return null;

          const _areaConfig = finalPanelConfig[area];

          return (
            <StyledGridArea
              key={area}
              $area={area}
              $isMobile={isMobile}
              $compactSpacing={compactSpacing}
              $noSpacing={noSpacing}
              style={{ gridArea: getUniqueGridArea(area) }}
            >
              {state.content}
            </StyledGridArea>
          );
        })}

        {/* Main content area */}
        <StyledGridArea
          $area={LayoutArea.CONTENT}
          $isMobile={isMobile}
          $compactSpacing={compactSpacing}
          $noSpacing={noSpacing}
          style={{ gridArea: getUniqueGridArea('content') }}
        >
          {panelStates.get(LayoutArea.CONTENT)?.content ?? children}
        </StyledGridArea>

        {/* Mobile overlay sidebars */}
        {isMobile && sidebarMode === 'overlay' && (
          <>
            {visiblePanels.includes(LayoutArea.LEFT_SIDEBAR) && (
              <StyledMobileOverlay $position="left">
                <StyledSidebar $position="left" $isMobile={true} $sidebarMode="overlay">
                  {panelStates.get(LayoutArea.LEFT_SIDEBAR)?.content}
                </StyledSidebar>
              </StyledMobileOverlay>
            )}
            {visiblePanels.includes(LayoutArea.RIGHT_SIDEBAR) && (
              <StyledMobileOverlay $position="right">
                <StyledSidebar $position="right" $isMobile={true} $sidebarMode="overlay">
                  {panelStates.get(LayoutArea.RIGHT_SIDEBAR)?.content}
                </StyledSidebar>
              </StyledMobileOverlay>
            )}
          </>
        )}

        {/* Debug system now managed at application-level via DebugFeatureProvider */}
      </StyledTabbedLayout>
    );
  }

  // Traditional panel layout (existing functionality) with page foundation
  return (
    <StyledTabbedLayout
      className={className}
      $isMobile={isMobile}
      $hasLeftSidebar={false}
      $hasRightSidebar={false}
      $sidebarMode={sidebarMode}
      $compactSpacing={compactSpacing}
      $noSpacing={noSpacing}
    >
      <PanelGroup autoSaveId={autoSaveId} direction={direction} id={`panel-group-${autoSaveId}`}>
        {visiblePanels.map((area, index) => {
          const state = panelStates.get(area);
          if (!state?.content || area === LayoutArea.CONTENT) return null;

          const config = finalPanelConfig[area];
          const showHandle = index < visiblePanels.length - 1 && config.resizable;

          return (
            <React.Fragment key={area}>
              <Panel
                id={`panel-${area}-${autoSaveId}`}
                order={index}
                defaultSize={typeof config.defaultSize === 'number' ? config.defaultSize : config.defaultSize?.value}
                minSize={typeof config.minSize === 'number' ? config.minSize : config.minSize?.value}
                maxSize={typeof config.maxSize === 'number' ? config.maxSize : config.maxSize?.value}
                collapsible={config.collapsible}
                onResize={(size) => onPanelChange?.(area, true, size)}
              >
                {state.content}
              </Panel>
              {showHandle && <PanelResizeHandle />}
            </React.Fragment>
          );
        })}

        {/* Always render content panel */}
        <Panel id={`panel-content-${autoSaveId}`} order={visiblePanels.length}>
          {children}
        </Panel>

        {/* Add final resize handle only if there are resizable panels */}
        {visiblePanels.some((area) => finalPanelConfig[area]?.resizable) && <PanelResizeHandle />}

        {/* Debug system now managed at application-level via DebugFeatureProvider */}
      </PanelGroup>
    </StyledTabbedLayout>
  );
};

/**
 * Main AutoPanelLayout component with tab support.
 * Wraps the renderer in AutoPanelProvider for state management.
 * Optimized for stability with TanStack Query and prevents infinite re-renders.
 */
export const AutoPanelLayoutTabbed: React.FC<AutoPanelLayoutProps> = function AutoPanelLayoutTabbed(props) {
  // Memoize the onPanelChange handler to prevent AutoPanelProvider re-renders
  const stableOnPanelChange = useCallback(
    (area: LayoutArea, isVisible: boolean, size?: number) => {
      props.onPanelChange?.(area, isVisible, size);
    },
    [props],
  );

  return <AutoPanelLayoutTabbedRenderer {...props} onPanelChange={stableOnPanelChange} />;
};

// Styled Components
interface StyledProps {
  $isMobile: boolean;
  $hasLeftSidebar?: boolean;
  $hasRightSidebar?: boolean;
  $sidebarMode?: 'overlay' | 'stack' | 'hidden';
  $compactSpacing?: boolean;
  $noSpacing?: boolean;
  $area?: LayoutArea;
  $position?: 'left' | 'right';
}

const StyledTabbedLayout = styled.div<StyledProps>`
  /* Essential page-level foundation using theme tokens */
  display: grid;
  min-height: 0;
  /* Allow content to determine height - don't force 100% for stacked layouts */
  height: auto;
  overflow: visible; /* Allow content overflow for proper stacking */

  /* Isolation: Reset CSS to prevent interference from parent layouts */
  position: relative;
  contain: layout style;
  isolation: isolate;

  /* Use theme typography tokens */
  font-family: ${({ theme }) => theme.fontFamilies.body};
  font-size: ${({ theme }) => theme.fontSizes.body};
  line-height: ${({ theme }) => theme.lineHeights.body};
  color: ${({ theme }) => theme.colors.textPrimary};

  /* AutoPanelLayout-specific spacing using theme tokens */
  gap: ${({ theme, $compactSpacing, $noSpacing }) => {
    if ($noSpacing) return '0';
    if ($compactSpacing) return theme.space.spacing10;
    return theme.space.spacing20;
  }};

  /* Responsive gap adjustment for mobile */
  @media (max-width: 768px) {
    gap: ${({ theme, $compactSpacing, $noSpacing }) => {
      if ($noSpacing) return '0';
      if ($compactSpacing) return theme.space.spacing10;
      return theme.space.spacing20;
    }};
  }

  /* Vertical clamp support */
  ${BreakpointSelectors.VerticalClamp} & {
    overflow-y: hidden;
  }

  /* Ensure child components are visible and properly styled */
  > * {
    min-height: 0;
    min-width: 0;
  }
`;

const StyledGridArea = styled.div<StyledProps>`
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;

  overflow: ${({ $area }) => {
    // Fixed areas don't need scrolling
    if (
      $area === LayoutArea.TABS ||
      $area === LayoutArea.HEADER ||
      $area === LayoutArea.BREADCRUMB ||
      $area === LayoutArea.SECONDARY_TABS ||
      $area === LayoutArea.ACTION_BUTTONS ||
      $area === LayoutArea.FOOTER
    ) {
      return 'visible';
    }
    return 'auto';
  }};

  padding: ${({ theme, $area, $compactSpacing, $noSpacing }) => {
    if ($noSpacing) return '0';

    // Different padding for different areas using theme tokens
    if ($area === LayoutArea.CONTENT) {
      return $compactSpacing ? `0 ${theme.space.spacing10}` : `0 ${theme.space.spacing20}`;
    }

    if ($area === LayoutArea.TABS || $area === LayoutArea.SECONDARY_TABS || $area === LayoutArea.ACTION_BUTTONS) {
      return $compactSpacing ? `${theme.space.spacing10} ${theme.space.spacing10}` : `${theme.space.spacing10} ${theme.space.spacing20}`;
    }

    if ($area === LayoutArea.HEADER || $area === LayoutArea.BREADCRUMB || $area === LayoutArea.FOOTER) {
      return $compactSpacing ? `${theme.space.spacing10} ${theme.space.spacing20}` : `${theme.space.spacing20} ${theme.space.spacing20}`;
    }

    return theme.space.spacing10; // Default padding using theme
  }};

  border-bottom: ${({ theme, $area }) => {
    if ($area === LayoutArea.TABS || $area === LayoutArea.HEADER || $area === LayoutArea.BREADCRUMB || $area === LayoutArea.SUBHEADER) {
      return `1px solid ${theme.colors.borderBase}`;
    }
    return 'none';
  }};

  border-top: ${({ theme, $area }) => {
    if ($area === LayoutArea.FOOTER) {
      return `1px solid ${theme.colors.borderBase}`;
    }
    return 'none';
  }};

  @media (max-width: 768px) {
    padding: ${({ theme, $area, $compactSpacing, $noSpacing }) => {
      if ($noSpacing) return '0';

      if ($area === LayoutArea.CONTENT) {
        return $compactSpacing ? `0 ${theme.space.spacing10}` : `0 ${theme.space.spacing10}`;
      }

      if ($area === LayoutArea.TABS || $area === LayoutArea.SECONDARY_TABS || $area === LayoutArea.ACTION_BUTTONS) {
        return $compactSpacing ? `${theme.space.spacing10} ${theme.space.spacing10}` : `${theme.space.spacing10} ${theme.space.spacing10}`;
      }

      return theme.space.spacing10;
    }};
  }
`;

const StyledSidebar = styled.div<StyledProps>`
  border: 1px solid ${({ theme }) => theme.colors.borderBase};
  display: flex;
  flex-direction: column;
  min-height: 0;

  ${({ theme, $position }) =>
    $position === 'left' ? `border-right: 2px solid ${theme.colors.borderBase};` : `border-left: 2px solid ${theme.colors.borderBase};`}

  ${({ $isMobile, $sidebarMode, $position, theme }) =>
    $isMobile &&
    $sidebarMode === 'overlay' &&
    `
    position: fixed;
    top: 0;
    bottom: 0;
    width: ${theme.sizes.sidebarWidth || '280px'};
    z-index: 1000;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateX(${$position === 'left' ? '-100%' : '100%'});
    transition: transform 0.3s ease-in-out;

    &.open {
      transform: translateX(0);
    }
  `}

  overflow-y: auto;
  overflow-x: hidden;

  /* Ensure content inside sidebars is properly displayed */
  > * {
    min-height: 0;
    flex-shrink: 0;
  }
`;

const StyledMobileOverlay = styled.div<{ $position: 'left' | 'right' }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease-in-out;

  &.open {
    opacity: 1;
    pointer-events: auto;
  }
`;
